import { apiClient } from '@borradh-workspace/api-client';
import {
  type ConnectionHealthResponse,
  connectionHealthResponseSchema,
} from '@borradh-workspace/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '@/lib/query-keys';

interface UseRefreshConnectionHealthOptions {
  onSuccess?: (data: ConnectionHealthResponse) => void;
}

/**
 * "Check again" — re-probes every connection for this organisation now.
 *
 * The sweep runs every six hours, which is the wrong latency for the moment
 * someone has just reconnected Instagram or paid Meta's bill: without this they
 * either wait, or decide the fix did not work and open a ticket.
 *
 * The response IS the fresh health payload, so it is written straight into the
 * cache rather than triggering a second round trip.
 */
export const useRefreshConnectionHealth = (
  options?: UseRefreshConnectionHealthOptions
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.post<ConnectionHealthResponse>(
        'integrations/connection-health/refresh',
        undefined,
        { schema: connectionHealthResponseSchema }
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.integrations.connectionHealth(), data);
      // A verdict can flip a provider card's own status too — reconcile writes
      // `tokenStatus` back to each integration row.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.integrations.all(),
      });

      toast.success(
        data.allHealthy
          ? 'Everything is connected'
          : data.problems.length === 1
            ? 'Still 1 thing to fix'
            : `Still ${data.problems.length} things to fix`
      );
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not check your connections');
    },
  });

  return {
    refreshConnectionHealth: mutation.mutate,
    isRefreshing: mutation.isPending,
  };
};
